import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { AssetsLibraryItemState } from '../../../../../../music24-api/src/kit/assetsLibrary/assetsLibraryTypes';
import { TypedMemo } from '../../../common/types';
import { WorkRow } from './studioWorkModel';
import { workPageMessages } from './workPageMessages';
import { WorkRowText } from './workRowText';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Work.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WorkRowStateProps {
  state: AssetsLibraryItemState;
  /** The step, progress and failure model of the row; null for a reel that has no such model. */
  row: WorkRow | null;
  text: WorkRowText | null;
  /** A finished row: "12 min. ago"; else null. */
  ago: string | null;
}

/**
 * The state column of a Work row (design assets-rethink e1 `.wk-s`).
 * - In progress: the step ("Rendering part 4 of 8 · 42%"; the percent only in the render step),
 *   the bar, then the time left and the queue hint.
 * - Failed: the reason and the chip "Credit back" or "No credit used".
 * - Finished: "Ready" and the age.
 */
const WorkRowStateComponent: React.FC<WorkRowStateProps> = ({ state, row, text, ago }) => {
  const intl = useIntl();
  if (state === 'ready') {
    return (
      <div className={styles.state}>
        <span>{[text?.step, ago].filter(Boolean).join(' · ')}</span>
      </div>
    );
  }
  if (state === 'failed') {
    return (
      <div className={styles.state}>
        <span className={styles.why}>{withBrandWord(text?.why ?? text?.step)}</span>
        {text !== null && text.refund !== null && <span className={styles.chip}>{text.refund}</span>}
      </div>
    );
  }

  const percent = row !== null && row.stage === 'render' ? Math.max(0, Math.min(100, Math.round(row.progress))) : 0;
  const step =
    text !== null && percent > 0
      ? intl.formatMessage(workPageMessages.pageStepPercent, { step: text.step, percent })
      : (text?.step ?? null);
  const hint = [text?.eta, text?.hint].filter(Boolean).join(' · ');
  return (
    <div className={styles.state}>
      {step !== null && <span className={clsx(styles.step, state === 'queued' && styles.stepQueued)}>{step}</span>}
      <div className={styles.bar} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
        <i style={{ width: `${percent}%` }} />
      </div>
      {hint !== '' && <span>{hint}</span>}
    </div>
  );
};

export const WorkRowState = TypedMemo(WorkRowStateComponent);
